import React, { useState, useEffect } from 'react';
import api from '../../../api';

/**
 * Server Section Component
 * Manages servers for branches with per-branch servers
 */
const ServerSection = ({
  branchId,
  companyId,
  onServerChange,
}) => {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingServer, setEditingServer] = useState(null);
  const [formData, setFormData] = useState({
    serverName: '',
    ipAddress: '',
    username: '',
    password: '',
    domain: '',
    serverRoles: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState({});

  // Fetch servers for this branch
  useEffect(() => {
    const fetchServers = async () => {
      if (!branchId) return;
      try {
        const res = await api.get(`/companies/branch/${branchId}/servers`);
        setServers(res.data);
      } catch (e) {
        console.error('Failed to fetch branch servers', e);
      } finally {
        setLoading(false);
      }
    };
    fetchServers();
  }, [branchId]);

  const resetForm = () => {
    setFormData({
      serverName: '',
      ipAddress: '',
      username: '',
      password: '',
      domain: '',
      serverRoles: '',
      notes: ''
    });
    setEditingServer(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.serverName) {
      alert('Server name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingServer) {
        // Update existing server
        const res = await api.put(`/companies/server/${editingServer.id}`, formData);
        setServers(prev => prev.map(s => s.id === editingServer.id ? res.data : s));
      } else {
        // Create new server
        const res = await api.post(`/companies/branch/${branchId}/servers`, formData);
        setServers(prev => [...prev, res.data]);
      }
      resetForm();
      if (onServerChange) onServerChange();
    } catch (e) {
      console.error('Failed to save server', e);
      alert('Failed to save server');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (server) => {
    setFormData({
      serverName: server.serverName || '',
      ipAddress: server.ipAddress || '',
      username: server.username || '',
      password: server.password || '',
      domain: server.domain || '',
      serverRoles: server.serverRoles || '',
      notes: server.notes || ''
    });
    setEditingServer(server);
    setShowForm(true);
  };

  const handleDelete = async (server) => {
    if (!window.confirm(`Delete server "${server.serverName}"?`)) return;
    try {
      await api.delete(`/companies/branch/${branchId}/servers/${server.id}`);
      setServers(prev => prev.filter(s => s.id !== server.id));
      if (onServerChange) onServerChange();
    } catch (e) {
      console.error('Failed to delete server', e);
      alert('Failed to delete server');
    }
  };

  const toggleComplete = async (server) => {
    try {
      const res = await api.put(`/companies/server/${server.id}`, {
        completed: !server.completed
      });
      setServers(prev => prev.map(s => s.id === server.id ? res.data : s));
    } catch (e) {
      console.error('Failed to toggle complete', e);
    }
  };

  const togglePasswordVisibility = (serverId) => {
    setShowPassword(prev => ({ ...prev, [serverId]: !prev[serverId] }));
  };

  if (loading) {
    return (
      <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6 mt-8">
        <div className="animate-pulse">
          <div className="h-6 bg-white/10 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-white/10 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6 mt-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <span>🖥️</span>
            Servers for this Branch
          </h3>
          <p className="text-slate-400 text-sm mt-1">
            {servers.length} server{servers.length !== 1 ? 's' : ''} configured
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <span>+</span>
            Add Server
          </button>
        )}
      </div>

      {/* Add/Edit Server Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="mb-6 bg-slate-800/50 rounded-lg p-4 border border-white/10">
          <h4 className="text-white font-semibold mb-4">
            {editingServer ? 'Edit Server' : 'Add New Server'}
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Server Name *</label>
              <input
                type="text"
                value={formData.serverName}
                onChange={(e) => setFormData({ ...formData, serverName: e.target.value })}
                className="w-full px-3 py-2 bg-slate-700/50 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                placeholder="e.g., DC01, FileServer"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-1">IP Address</label>
              <input
                type="text"
                value={formData.ipAddress}
                onChange={(e) => setFormData({ ...formData, ipAddress: e.target.value })}
                className="w-full px-3 py-2 bg-slate-700/50 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                placeholder="e.g., 192.168.1.10"
              />
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-1">Domain</label>
              <input
                type="text"
                value={formData.domain}
                onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                className="w-full px-3 py-2 bg-slate-700/50 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                placeholder="e.g., company.local"
              />
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-1">Server Roles</label>
              <input
                type="text"
                value={formData.serverRoles}
                onChange={(e) => setFormData({ ...formData, serverRoles: e.target.value })}
                className="w-full px-3 py-2 bg-slate-700/50 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                placeholder="e.g., DC, DNS, DHCP, File"
              />
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-1">Admin Username</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="w-full px-3 py-2 bg-slate-700/50 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                placeholder="e.g., Administrator"
              />
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-1">Admin Password</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-3 py-2 bg-slate-700/50 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                placeholder="••••••••"
              />
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm text-slate-400 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 bg-slate-700/50 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                placeholder="Additional notes about this server..."
              />
            </div>
          </div>
          
          <div className="flex gap-3 mt-4">
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white rounded-lg font-medium transition-colors"
            >
              {saving ? 'Saving...' : (editingServer ? 'Update Server' : 'Add Server')}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Server List */}
      {servers.length === 0 && !showForm ? (
        <div className="text-center py-8 text-slate-400">
          <div className="text-4xl mb-2">🖥️</div>
          <p>No servers configured for this branch yet.</p>
          <p className="text-sm">Click "Add Server" to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {servers.map((server) => (
            <div
              key={server.id}
              className={`p-4 rounded-lg border transition-all ${
                server.completed
                  ? 'bg-emerald-900/20 border-emerald-500/30'
                  : 'bg-slate-800/50 border-white/10 hover:border-white/20'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggleComplete(server)}
                      className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                        server.completed
                          ? 'bg-emerald-500 border-emerald-500 text-white'
                          : 'border-slate-500 hover:border-blue-500'
                      }`}
                    >
                      {server.completed && (
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                    <h4 className={`font-semibold ${server.completed ? 'text-emerald-300' : 'text-white'}`}>
                      {server.serverName || 'Unnamed Server'}
                    </h4>
                    {server.serverRoles && (
                      <span className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded">
                        {server.serverRoles}
                      </span>
                    )}
                  </div>
                  
                  <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                    {server.ipAddress && (
                      <div>
                        <span className="text-slate-500">IP:</span>{' '}
                        <span className="text-slate-300">{server.ipAddress}</span>
                      </div>
                    )}
                    {server.domain && (
                      <div>
                        <span className="text-slate-500">Domain:</span>{' '}
                        <span className="text-slate-300">{server.domain}</span>
                      </div>
                    )}
                    {server.username && (
                      <div>
                        <span className="text-slate-500">User:</span>{' '}
                        <span className="text-slate-300">{server.username}</span>
                      </div>
                    )}
                    {server.password && (
                      <div className="flex items-center gap-1">
                        <span className="text-slate-500">Pass:</span>{' '}
                        <span className="text-slate-300 font-mono">
                          {showPassword[server.id] ? server.password : '••••••••'}
                        </span>
                        <button
                          onClick={() => togglePasswordVisibility(server.id)}
                          className="text-slate-400 hover:text-white ml-1"
                          title={showPassword[server.id] ? 'Hide password' : 'Show password'}
                        >
                          {showPassword[server.id] ? '👁️' : '👁️‍🗨️'}
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {server.notes && (
                    <p className="mt-2 text-sm text-slate-400 italic">{server.notes}</p>
                  )}
                </div>
                
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => handleEdit(server)}
                    className="p-2 text-slate-400 hover:text-blue-400 hover:bg-blue-400/10 rounded transition-colors"
                    title="Edit"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDelete(server)}
                    className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-400/10 rounded transition-colors"
                    title="Delete"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ServerSection;
